import { type MaybeRef, computed, unref } from 'vue';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { type MutationFunction, type MutationKey } from '@tanstack/vue-query';
import { isNil, some } from 'lodash-es';
import { type NullableMaybeRef } from '../utils/types';
import { type ApiError, type PaginationMeta } from './http';

export function useInvalidatingMutation<
  TMutationKey extends MutationKey,
  TData = void,
  TVariables = void,
>(
  invalidatable:
    | TMutationKey
    | TMutationKey
    | ((data: TData) => TMutationKey | TMutationKey[]),
  mutationFn: MutationFunction<TData, TVariables>
) {
  const queryClient = useQueryClient();
  return useMutation<TData, ApiError, TVariables>(mutationFn, {
    onSuccess: (data) => {
      const invalidatableCallResult =
        typeof invalidatable === 'function'
          ? invalidatable(data)
          : invalidatable;
      const keysToInvalidate = Array.isArray(invalidatableCallResult)
        ? invalidatableCallResult
        : [invalidatableCallResult];

      for (const key of keysToInvalidate) queryClient.invalidateQueries(key);
    },
  });
}

export function tryGetNextPage(meta: PaginationMeta) {
  return meta.currentPage !== meta.lastPage ? meta.currentPage + 1 : undefined;
}

export function nonNullableMaybeRef<T>(value: MaybeRef<T | undefined>) {
  return value as MaybeRef<NonNullable<T>>;
}

export function dependOnValues(values: NullableMaybeRef<unknown>[]) {
  return computed(() => !some(values, (value) => isNil(unref(value))));
}
